import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserProfile } from '../types';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import Avatar from './Avatar';
import Tooltip from './Tooltip';
import { 
  LayoutDashboard, 
  Users, 
  UserCircle, 
  LogOut, 
  Menu, 
  X, 
  Hash, 
  ChevronDown, 
  Sun, 
  Moon, 
  CalendarDays, 
  BarChart3, 
  Languages, 
  LifeBuoy, 
  BookOpen, 
  Library, 
  Store, 
  Sparkles, 
  ChevronLeft, 
  PanelLeftClose, 
  PanelLeftOpen,
  MessageSquare
} from 'lucide-react';
import { useTranslation } from '../lib/translations';

interface LayoutProps {
  children: React.ReactNode;
  profile: UserProfile | null;
}

// Added NavItem interface for proper typing
interface NavItem {
  label: string;
  icon: any;
  path: string;
  id: string;
}

// Fixed: Centering logic for collapsed mode. Removed gap and adjusted padding.
const NavLink: React.FC<{ item: NavItem; collapsed: boolean; isActive: boolean }> = ({ item, collapsed, isActive }) => {
  const Icon = item.icon;
  
  const content = (
    <Link
      to={item.path}
      id={item.id}
      className={`
        relative flex items-center rounded-xl transition-all duration-300 group
        ${collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-3 py-3'}
        ${isActive 
          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30' 
          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-slate-100'}
      `}
    >
      <Icon size={20} strokeWidth={isActive ? 3 : 2.5} className="shrink-0" />
      {!collapsed && (
        <span className="text-[10px] font-black uppercase tracking-widest truncate animate-in fade-in slide-in-from-left-2 duration-300">
          {item.label}
        </span>
      )}
      {isActive && collapsed && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-l-full" />
      )}
    </Link>
  );

  return collapsed ? (
    <Tooltip content={item.label} position="right" className="w-full">
      {content}
    </Tooltip>
  ) : content;
};

const Layout: React.FC<LayoutProps> = ({ children, profile }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const { language, setLanguage, t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Typed navItems with NavItem interface
  const navItems: NavItem[] = [
    ...(profile?.role !== 'teacher' ? [
      { label: t.nav.marketplace, icon: Store, path: '/teachers', id: 'nav-marketplace' }
    ] : []),
    ...(profile?.role !== null ? [
      { label: t.nav.dashboard, icon: LayoutDashboard, path: '/', id: 'nav-dashboard' }
    ] : []),
    ...(profile?.role === 'teacher' ? [
      { label: t.nav.students, icon: Users, path: '/students', id: 'nav-students' },
      { label: t.nav.planner, icon: CalendarDays, path: '/planner', id: 'nav-planner' },
      { label: t.nav.programs, icon: Library, path: '/programs', id: 'nav-curriculum' },
      { label: t.nav.insights, icon: BarChart3, path: '/stats', id: 'nav-insights' },
      { label: t.nav.reviews, icon: MessageSquare, path: '/reviews', id: 'nav-reviews' },
      { label: t.nav.publicProfile, icon: Sparkles, path: '/public-profile', id: 'nav-public-presence' }
    ] : []),
  ];

  const handleLogout = () => {
    signOut(auth);
    sessionStorage.removeItem('google_access_token');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300 flex">
      {/* Mobile Header (Fixed Top) */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 glass-card z-50 px-4 flex items-center justify-between border-b border-white/40 dark:border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-purple-600 to-indigo-700 rounded shadow-lg">
            <Hash size={16} className="text-white" />
          </div>
          <span className="text-sm font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
        </Link>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg text-slate-600 dark:text-slate-400"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* Desktop Fixed Sidebar */}
      <aside 
        className={`
          hidden lg:flex fixed top-0 left-0 h-screen z-50 flex-col transition-all duration-500 ease-in-out border-r border-slate-200/60 dark:border-white/5 glass-card !rounded-none
          ${isSidebarCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Sidebar Logo */}
        <div className={`p-5 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          <Link to="/" className={`flex items-center transition-all group ${isSidebarCollapsed ? 'gap-0' : 'gap-3'}`}>
            <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg shrink-0 group-hover:scale-105 transition-transform">
              <Hash size={24} className="text-white" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex flex-col leading-none animate-in fade-in duration-500 truncate">
                <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
                <span className="text-[8px] uppercase tracking-widest text-slate-400 font-black">{t.nav.studioHub}</span>
              </div>
            )}
          </Link>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 px-3 space-y-2 mt-4 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {navItems.map((item) => (
            <NavLink 
              key={item.path} 
              item={item} 
              collapsed={isSidebarCollapsed} 
              isActive={location.pathname === item.path}
            />
          ))}
        </nav>

        {/* Sidebar Footer Controls */}
        <div className="p-3 border-t border-slate-200 dark:border-white/5 space-y-2">
          <button
            onClick={() => setIsDark(!isDark)}
            className={`flex items-center w-full p-3 rounded-xl transition-all ${isSidebarCollapsed ? 'justify-center gap-0' : 'gap-3'} text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white`}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
            {!isSidebarCollapsed && <span className="text-[10px] font-black uppercase tracking-widest">{t.nav.toggleTheme}</span>}
          </button>

          <button
            onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
            className={`flex items-center w-full p-3 rounded-xl transition-all ${isSidebarCollapsed ? 'justify-center gap-0' : 'gap-3'} text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white`}
          >
            <Languages size={20} />
            {!isSidebarCollapsed && <span className="text-[10px] font-black uppercase tracking-widest">{language.toUpperCase()}</span>}
          </button>

          <div className="h-px bg-slate-200 dark:bg-white/5 mx-2" />

          {/* Profile Section in Sidebar */}
          <div className="relative">
            <button 
              id="nav-profile"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className={`flex items-center w-full p-2 rounded-xl transition-all group ${isSidebarCollapsed ? 'justify-center gap-0' : 'gap-3'} hover:bg-slate-100 dark:hover:bg-white/5`}
            >
              <Avatar 
                src={profile?.photoURL} 
                name={profile?.displayName} 
                className="w-10 h-10 rounded-xl" 
              />
              {!isSidebarCollapsed && (
                <div className="flex-1 text-left min-w-0 animate-in fade-in duration-300">
                  <p className="text-xs font-black text-slate-900 dark:text-slate-100 leading-none mb-1 truncate">{profile?.displayName?.split(' ')[0] || t.common.anonymous}</p>
                  <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest leading-none truncate">{profile?.role === 'teacher' ? t.common.teacher : t.common.student}</p>
                </div>
              )}
              {!isSidebarCollapsed && <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />}
            </button>

            {isProfileOpen && !isSidebarCollapsed && (
              <div className="absolute bottom-full left-0 mb-2 w-full glass-card rounded-xl shadow-2xl border-white/20 dark:border-white/5 p-2 z-20 animate-in slide-in-from-bottom-2 duration-200">
                <Link to="/profile" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-xs font-bold">
                  <UserCircle size={16} /> {t.nav.profile}
                </Link>
                {profile?.role === 'teacher' && (
                  <Link to="/guide" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-xs font-bold">
                    <BookOpen size={16} /> {t.nav.guide}
                  </Link>
                )}
                <Link to="/support" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-xs font-bold">
                  <LifeBuoy size={16} /> {t.nav.support}
                </Link>
                <div className="h-px bg-slate-200 dark:bg-white/5 my-1" />
                <button onClick={handleLogout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 transition-all text-xs font-bold">
                  <LogOut size={16} /> {t.nav.signOut}
                </button>
              </div>
            )}

            {isProfileOpen && isSidebarCollapsed && (
              <div className="absolute left-full bottom-0 ml-3 w-56 glass-card rounded-xl shadow-2xl border-white/20 dark:border-white/5 p-2 z-20 animate-in slide-in-from-left-2 duration-200">
                <Link to="/profile" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-sm font-bold">
                  <UserCircle size={18} /> {t.nav.profile}
                </Link>
                {profile?.role === 'teacher' && (
                  <Link to="/guide" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-sm font-bold">
                    <BookOpen size={18} /> {t.nav.guide}
                  </Link>
                )}
                <Link to="/support" onClick={() => setIsProfileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-purple-600 transition-all text-sm font-bold">
                  <LifeBuoy size={18} /> {t.nav.support}
                </Link>
                <div className="h-px bg-slate-200 dark:bg-white/5 my-1 mx-2" />
                <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 transition-all text-sm font-bold">
                  <LogOut size={18} /> {t.nav.signOut}
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="flex items-center justify-center w-full p-3 text-slate-400 hover:text-purple-600 transition-colors"
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isSidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>
      </aside>

      {/* Mobile Overlay Menu */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-[280px] bg-white dark:bg-[#020617] shadow-2xl p-6 flex flex-col border-l border-white/10 animate-in slide-in-from-right duration-500">
            <div className="flex items-center justify-between mb-10">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg">
                  <Hash size={20} className="text-white" />
                </div>
                <span className="text-lg font-black text-slate-900 dark:text-white">SharpNote</span>
              </Link>
              <button onClick={() => setIsMenuOpen(false)} className="p-2 text-slate-400"><X size={24} /></button>
            </div>
            
            <nav className="flex-1 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`
                    flex items-center gap-4 px-4 py-3 rounded-xl transition-all
                    ${location.pathname === item.path 
                      ? 'bg-purple-600 text-white shadow-lg' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5'}
                  `}
                >
                  <item.icon size={20} />
                  <span className="text-xs font-black uppercase tracking-widest">{item.label}</span>
                </Link>
              ))}
              {profile?.role === 'teacher' && (
                <Link
                  to="/guide"
                  onClick={() => setIsMenuOpen(false)}
                  className={`
                    flex items-center gap-4 px-4 py-3 rounded-xl transition-all
                    ${location.pathname === '/guide' 
                      ? 'bg-purple-600 text-white shadow-lg' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5'}
                  `}
                >
                  <BookOpen size={20} />
                  <span className="text-xs font-black uppercase tracking-widest">{t.nav.guide}</span>
                </Link>
              )}
            </nav>

            <div className="pt-6 border-t border-slate-100 dark:border-white/5 space-y-4">
               <div className="flex items-center gap-4 px-4">
                 <Avatar src={profile?.photoURL} name={profile?.displayName} className="w-10 h-10 rounded-xl" />
                 <div className="min-w-0">
                    <p className="text-sm font-black text-slate-900 dark:text-white truncate">{profile?.displayName}</p>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{profile?.role}</p>
                 </div>
               </div>
               <div className="grid grid-cols-2 gap-2">
                 <Link to="/profile" onClick={() => setIsMenuOpen(false)} className="flex items-center justify-center gap-2 p-3 bg-slate-50 dark:bg-white/5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500"><UserCircle size={16} /> Profile</Link>
                 <button onClick={handleLogout} className="flex items-center justify-center gap-2 p-3 bg-red-50 dark:bg-red-500/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-red-600"><LogOut size={16} /> Exit</button>
               </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main 
        className={`flex-1 transition-all duration-500 ease-in-out ${
          profile 
            ? isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
            : 'ml-0'
        }`}
        style={{ 
          paddingTop: profile ? '6rem' : '0'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 pb-20">
          {children}
        </div>
      </main>
      
      {/* Background Decor */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -top-[15%] left-[20%] w-[60%] h-[60%] bg-purple-600/10 dark:bg-purple-600/20 blur-[120px] rounded-full animate-pulse opacity-40"></div>
        <div className="absolute top-[20%] -left-[10%] w-[50%] h-[50%] bg-brand-accent/5 dark:bg-brand-accent/10 blur-[120px] rounded-full opacity-40"></div>
        <div className="absolute bottom-[0%] -right-[15%] w-[45%] h-[45%] bg-brand-accent/5 dark:bg-brand-accent/10 blur-[120px] rounded-full opacity-40"></div>
      </div>
    </div>
  );
};

export default Layout;